import { useState } from 'react';
import { theme } from '../../theme.js';
import { useAuth } from '../../context/AuthContext.js';
import {
  useEquipment, useCategories, useCreateEquipment, useUpdateEquipment, useRetireEquipment,
  useCreateCategory, useUpdateCategory, useRetireCategory,
} from '../../lib/queries.js';
import { Icon } from '../../components/Icon.js';
import { EquipmentHistory } from '../../components/EquipmentHistory.js';
import { EquipmentPlacements } from '../../components/EquipmentPlacements.js';
import type { Category, Equipment, EquipKind } from '@keetcheck/shared';

export function EquipmentPage() {
  const { user } = useAuth();
  const orgId = user?.orgId ?? '';

  const [categoryId, setCategoryId] = useState('');
  const [type, setType]             = useState('');
  const [search, setSearch]         = useState('');
  const [kind, setKind]             = useState('item'); // checks/readings live here too; show equipment first
  const [showForm, setShowForm]     = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  const [editingId, setEditingId]   = useState<string | null>(null);

  const { data: categories = [] } = useCategories(orgId);
  const { data: all = [], isLoading } = useEquipment(orgId, {
    categoryId: categoryId || undefined,
    type: type || undefined,
    kind: kind || undefined,
  });
  const createMut = useCreateEquipment(orgId);
  // Quick filter: every word must appear in the name, serial, category or place.
  const words = search.toLowerCase().split(/\s+/).filter(Boolean);
  const equipment = words.length === 0 ? all : all.filter((eq) => {
    const text = [eq.name, eq.serialNumber, eq.categoryName, ...(eq.places ?? [])].join(' ').toLowerCase();
    return words.every((w) => text.includes(w));
  });

  return (
    <div style={{ flex: 1, background: theme.surface }}>
      {/* toolbar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '10px 16px', flexWrap: 'wrap',
        borderBottom: `1px solid ${theme.border}`,
        background: theme.bg,
      }}>
        <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} style={chipSelect}>
          <option value="">All categories</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={kind} onChange={(e) => setKind(e.target.value)} style={chipSelect} aria-label="Kind">
          <option value="item">Equipment</option>
          <option value="task">Checks</option>
          <option value="reading">Readings</option>
          <option value="">Everything</option>
        </select>
        <select value={type} onChange={(e) => setType(e.target.value)} style={chipSelect}>
          <option value="">All types</option>
          <option value="tracked">Tracked</option>
          <option value="pooled">Pooled</option>
        </select>
        <div style={{ flex: 1 }} />
        <button onClick={() => setShowCategories(!showCategories)} style={secondaryBtn}>
          Categories
        </button>
        <button onClick={() => setShowForm(!showForm)} style={addBtn}>
          <Icon name="plus" size={15} /> Add equipment
        </button>
      </div>

      {/* quick search */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px',
        borderBottom: `1px solid ${theme.border}`, background: theme.bg,
      }}>
        <Icon name="search" size={16} style={{ color: theme.textMuted, flexShrink: 0 }} />
        <input
          type="search" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, serial, category or place (e.g. tray 3)"
          aria-label="Search equipment"
          style={{
            flex: 1, minWidth: 0, padding: '8px 10px', fontSize: 14, fontFamily: theme.font,
            border: `1px solid ${theme.border}`, borderRadius: 8,
            background: theme.surface, color: theme.text,
          }}
        />
      </div>

      {showCategories && <CategoriesPanel orgId={orgId} categories={categories} />}

      {showForm && (
        <EquipmentForm
          categories={categories}
          submitting={createMut.isPending}
          onCancel={() => setShowForm(false)}
          onSubmit={(values) => createMut.mutate(values, { onSuccess: () => setShowForm(false) })}
        />
      )}

      {/* count */}
      <div style={{
        padding: '8px 18px', fontSize: 12, color: theme.textMuted, fontWeight: 600,
        background: theme.bg, borderBottom: `1px solid ${theme.border}`,
      }}>
        {isLoading ? 'Loading…' : `${equipment.length} item${equipment.length !== 1 ? 's' : ''} · tap one to edit`}
      </div>

      {/* list */}
      {!isLoading && equipment.length === 0 && (
        <div style={{ padding: '40px 20px', textAlign: 'center', color: theme.textMuted, fontSize: 14 }}>
          No equipment found.
        </div>
      )}
      {equipment.map((eq, i) => (
        <div key={eq.id}>
          <EquipmentRow
            eq={eq}
            last={i === equipment.length - 1 && editingId !== eq.id}
            onClick={() => setEditingId(editingId === eq.id ? null : eq.id)}
          />
          {editingId === eq.id && (
            <EditEquipment
              orgId={orgId} eq={eq} categories={categories}
              onDone={() => setEditingId(null)}
            />
          )}
        </div>
      ))}
    </div>
  );
}

// ── Create / edit ─────────────────────────────────────────────

type EquipmentValues = Partial<Omit<Equipment, 'categoryId' | 'serialNumber' | 'notes' | 'expiryDate' | 'serviceDueDate'>> & {
  categoryId?: string | null;
  serialNumber?: string | null;
  notes?: string | null;
  expiryDate?: string | null;
  serviceDueDate?: string | null;
};

function EditEquipment({
  orgId, eq, categories, onDone,
}: { orgId: string; eq: Equipment; categories: Category[]; onDone: () => void }) {
  const updateMut = useUpdateEquipment(orgId, eq.id);
  const retireMut = useRetireEquipment(orgId);
  return (
    <>
      <EquipmentForm
        initial={eq}
        categories={categories}
        submitting={updateMut.isPending || retireMut.isPending}
        onCancel={onDone}
        onSubmit={(values) => updateMut.mutate(values as Partial<Equipment>, { onSuccess: onDone })}
        onRetire={() => retireMut.mutate(eq.id, { onSuccess: onDone })}
      />
      <div style={{ padding: '12px 16px 16px', background: theme.surface2, borderBottom: `1px solid ${theme.border}` }}>
        <EquipmentPlacements orgId={orgId} equipmentId={eq.id} tracked={eq.equipmentType === 'tracked'} />
        <div style={{ height: 16 }} />
        <EquipmentHistory orgId={orgId} equipmentId={eq.id} title="Inspection history" />
      </div>
    </>
  );
}

/**
 * Shared create/edit form. On edit, emptied fields are sent as null so they
 * clear; the type is fixed once created (it would muddle past inspections).
 */
function EquipmentForm({
  initial, categories, submitting, onSubmit, onCancel, onRetire,
}: {
  initial?: Equipment;
  categories: Category[];
  submitting: boolean;
  onSubmit: (values: EquipmentValues) => void;
  onCancel: () => void;
  onRetire?: () => void;
}) {
  const editing = !!initial;
  const [name, setName]             = useState(initial?.name ?? '');
  const [eqType, setEqType]         = useState<'tracked' | 'pooled'>(initial?.equipmentType ?? 'tracked');
  const [catId, setCatId]           = useState(initial?.categoryId ?? '');
  const [serial, setSerial]         = useState(initial?.serialNumber ?? '');
  const [quantity, setQuantity]     = useState(String(initial?.quantity ?? 1));
  const [expiryDate, setExpiryDate] = useState(initial?.expiryDate?.slice(0, 10) ?? '');
  const [serviceDue, setServiceDue] = useState(initial?.serviceDueDate?.slice(0, 10) ?? '');
  const [notes, setNotes]           = useState(initial?.notes ?? '');
  const [kind, setKind]             = useState<EquipKind>(initial?.kind ?? 'item');
  const [confirmRetire, setConfirmRetire] = useState(false);

  // Empty → null when editing (clears the field), → omitted when creating.
  const opt = (v: string) => (v.trim() ? v.trim() : editing ? null : undefined);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSubmit({
      name: name.trim(),
      kind,
      ...(editing ? {} : { equipmentType: eqType }),
      categoryId: opt(catId),
      ...(eqType === 'tracked'
        ? { serialNumber: opt(serial) }
        : { quantity: parseInt(quantity, 10) || 1 }),
      expiryDate: opt(expiryDate),
      serviceDueDate: opt(serviceDue),
      notes: opt(notes),
    });
  }

  return (
    <form onSubmit={handleSubmit} style={{
      padding: '14px 16px', background: theme.surface2,
      borderBottom: `1px solid ${theme.border}`,
      display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      <input
        required value={name} onChange={(e) => setName(e.target.value)}
        placeholder="Equipment name" aria-label="Name"
        style={inputStyle}
      />
      <select value={kind} onChange={(e) => setKind(e.target.value as EquipKind)} aria-label="Kind" style={inputStyle}>
        <option value="item">Equipment — count / inspect it</option>
        <option value="task">Check — something to do or verify (lights, levels, tidy up)</option>
        <option value="reading">Reading — record a value (odometer, fuel level)</option>
      </select>
      <div style={{ display: 'flex', gap: 8 }}>
        <select
          value={eqType} disabled={editing}
          onChange={(e) => setEqType(e.target.value as 'tracked' | 'pooled')}
          title={editing ? 'Type can’t change once created' : undefined}
          style={{ ...inputStyle, flex: 1 }}
        >
          <option value="tracked">Tracked (serialised)</option>
          <option value="pooled">Pooled (qty)</option>
        </select>
        <select value={catId} onChange={(e) => setCatId(e.target.value)} style={{ ...inputStyle, flex: 1 }}>
          <option value="">No category</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>
      {eqType === 'tracked' ? (
        <input
          value={serial} onChange={(e) => setSerial(e.target.value)}
          placeholder="Serial number" aria-label="Serial number"
          style={inputStyle}
        />
      ) : (
        <input
          type="number" min={1} value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          placeholder="Quantity" aria-label="Quantity"
          style={inputStyle}
        />
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <label style={{ flex: 1 }}>
          <span style={fieldLabel}>Expiry date</span>
          <input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)}
            style={{ ...inputStyle, marginTop: 4 }} />
        </label>
        <label style={{ flex: 1 }}>
          <span style={fieldLabel}>Service due</span>
          <input type="date" value={serviceDue} onChange={(e) => setServiceDue(e.target.value)}
            style={{ ...inputStyle, marginTop: 4 }} />
        </label>
      </div>
      <textarea
        value={notes} onChange={(e) => setNotes(e.target.value)}
        placeholder="Notes (optional)" rows={2} aria-label="Notes"
        style={{ ...inputStyle, resize: 'none' }}
      />
      <div style={{ display: 'flex', gap: 10 }}>
        <button type="button" onClick={onCancel} style={cancelBtn}>Cancel</button>
        <button type="submit" disabled={submitting} style={submitBtnStyle}>
          {submitting ? 'Saving…' : editing ? 'Save' : 'Create'}
        </button>
      </div>
      {onRetire && (
        <RetireControl
          confirming={confirmRetire}
          onAsk={() => setConfirmRetire(true)}
          onCancel={() => setConfirmRetire(false)}
          onConfirm={onRetire}
          what="this equipment"
          detail="It’s taken off every asset layout and storage location, so it won’t appear in new checks. Past inspection results are kept."
        />
      )}
    </form>
  );
}

function RetireControl({
  confirming, onAsk, onCancel, onConfirm, what, detail,
}: {
  confirming: boolean; onAsk: () => void; onCancel: () => void; onConfirm: () => void;
  what: string; detail: string;
}) {
  if (!confirming) {
    return <button type="button" onClick={onAsk} style={retireLink}>Retire {what}</button>;
  }
  return (
    <div style={{
      padding: 10, borderRadius: 10, fontSize: 12, lineHeight: 1.4,
      background: theme.status.danger.bg, color: theme.status.danger.fg,
      display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
    }}>
      <span style={{ flex: '1 1 200px' }}>{detail}</span>
      <button type="button" onClick={onCancel} style={{ ...smallBtn, background: theme.surface }}>Keep</button>
      <button type="button" onClick={onConfirm}
        style={{ ...smallBtn, background: theme.status.danger.dot, color: '#fff', border: 'none' }}>
        Retire
      </button>
    </div>
  );
}

// ── Categories ────────────────────────────────────────────────

function CategoriesPanel({ orgId, categories }: { orgId: string; categories: Category[] }) {
  const createMut = useCreateCategory(orgId);
  const [name, setName] = useState('');

  return (
    <div style={{ background: theme.surface2, borderBottom: `1px solid ${theme.border}`, padding: '12px 16px' }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: theme.textDim, marginBottom: 8 }}>Categories</div>
      {categories.length === 0 && (
        <div style={{ fontSize: 13, color: theme.textMuted, marginBottom: 8 }}>No categories yet.</div>
      )}
      {categories.map((c) => <CategoryRow key={c.id} orgId={orgId} category={c} />)}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) createMut.mutate({ name: name.trim() }, { onSuccess: () => setName('') });
        }}
        style={{ display: 'flex', gap: 8, marginTop: 8 }}
      >
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="New category"
          aria-label="New category name" style={{ ...inputStyle, flex: 1 }} />
        <button type="submit" disabled={createMut.isPending || !name.trim()} style={smallBtn}>Add</button>
      </form>
    </div>
  );
}

function CategoryRow({ orgId, category }: { orgId: string; category: Category }) {
  const updateMut = useUpdateCategory(orgId);
  const retireMut = useRetireCategory(orgId);
  const [draft, setDraft] = useState(category.name);
  const [confirmRetire, setConfirmRetire] = useState(false);
  const changed = draft.trim() && draft.trim() !== category.name;

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
      <input value={draft} onChange={(e) => setDraft(e.target.value)} aria-label={`Rename ${category.name}`}
        style={{ ...inputStyle, flex: 1 }} />
      {changed && (
        <button type="button" onClick={() => updateMut.mutate({ id: category.id, name: draft.trim() })} style={smallBtn}>
          Save
        </button>
      )}
      {confirmRetire ? (
        <>
          <button type="button" onClick={() => setConfirmRetire(false)} style={smallBtn}>Keep</button>
          <button type="button" onClick={() => retireMut.mutate(category.id)}
            style={{ ...smallBtn, background: theme.status.danger.dot, color: '#fff', border: 'none' }}>
            Retire
          </button>
        </>
      ) : (
        <button aria-label="Retire category" type="button" onClick={() => setConfirmRetire(true)} title="Retire category" style={smallBtn}>
          <Icon name="trash" size={12} />
        </button>
      )}
    </div>
  );
}

// ── List row ──────────────────────────────────────────────────

function EquipmentRow({ eq, last, onClick }: { eq: Equipment; last: boolean; onClick: () => void }) {
  const expired = eq.expiryDate && new Date(eq.expiryDate) < new Date();
  const serviceOverdue = eq.serviceDueDate && new Date(eq.serviceDueDate) < new Date();
  return (
    <button onClick={onClick} style={{
      display: 'flex', alignItems: 'flex-start', gap: 12, width: '100%', textAlign: 'left',
      padding: '13px 18px', background: 'none', border: 'none', cursor: 'pointer', fontFamily: theme.font,
      borderBottom: last ? 'none' : `1px solid ${theme.border}`,
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: 8, flexShrink: 0,
        background: theme.surface2, border: `1px solid ${theme.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: theme.textMuted,
      }}>
        <Icon name={eq.equipmentType === 'tracked' ? 'package' : 'kit'} size={17} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: theme.text }}>{eq.name}</div>
        <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 2 }}>
          {[
            eq.categoryName,
            eq.serialNumber ? `#${eq.serialNumber}` : eq.quantity > 1 ? `× ${eq.quantity}` : null,
          ].filter(Boolean).join(' · ')}
        </div>
        {eq.places && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4, fontSize: 12, color: theme.textMuted, marginTop: 3 }}>
            <Icon name="pin" size={12} style={{ flexShrink: 0, marginTop: 2 }} />
            <span style={{ fontStyle: eq.places.length ? 'normal' : 'italic' }}>
              {eq.places.length === 0 ? 'Not placed'
                : eq.places.length <= 2 ? eq.places.join(' · ')
                : `${eq.places.length} places: ${eq.places.map((p) => p.split(' › ')[0]).filter((a, i, all) => all.indexOf(a) === i).join(', ')}`}
            </span>
          </div>
        )}
        {(expired || serviceOverdue) && (
          <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
            {expired && <Badge>EXPIRED</Badge>}
            {serviceOverdue && <Badge>SERVICE OVERDUE</Badge>}
          </div>
        )}
      </div>
      <span style={{
        fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999,
        background: theme.status.muted.bg, color: theme.status.muted.fg,
        alignSelf: 'center', flexShrink: 0,
      }}>
        {eq.kind === 'task' ? 'check' : eq.kind === 'reading' ? 'reading' : eq.equipmentType}
      </span>
    </button>
  );
}

function Badge({ children }: { children: React.ReactNode }) {
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 999,
      background: theme.status.danger.bg, color: theme.status.danger.fg,
    }}>{children}</span>
  );
}

const chipSelect: React.CSSProperties = {
  padding: '6px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600,
  border: `1px solid ${theme.border}`, background: theme.surface,
  color: theme.textDim, outline: 'none', fontFamily: theme.font,
};

const inputStyle: React.CSSProperties = {
  padding: '9px 12px', background: theme.surface,
  border: `1px solid ${theme.border}`, borderRadius: 10,
  fontSize: 13, color: theme.text, fontFamily: theme.font,
  outline: 'none', width: '100%', boxSizing: 'border-box',
};

const fieldLabel: React.CSSProperties = { fontSize: 11, color: theme.textDim, fontWeight: 600 };

const addBtn: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6,
  padding: '7px 14px', borderRadius: 8,
  background: theme.accent, border: 'none',
  color: theme.accentInk, fontSize: 13, fontWeight: 600,
  cursor: 'pointer', fontFamily: theme.font,
};

const secondaryBtn: React.CSSProperties = {
  padding: '7px 12px', borderRadius: 8,
  background: theme.surface, border: `1px solid ${theme.border}`,
  color: theme.textDim, fontSize: 13, fontWeight: 600,
  cursor: 'pointer', fontFamily: theme.font,
};

const smallBtn: React.CSSProperties = {
  padding: '7px 10px', borderRadius: 8, flexShrink: 0,
  background: theme.surface, border: `1px solid ${theme.border}`,
  color: theme.textDim, fontSize: 12, fontWeight: 600,
  cursor: 'pointer', fontFamily: theme.font,
  display: 'flex', alignItems: 'center',
};

const retireLink: React.CSSProperties = {
  alignSelf: 'flex-start', background: 'none', border: 'none', padding: 0,
  color: theme.status.danger.fg, fontSize: 12, fontWeight: 600,
  cursor: 'pointer', fontFamily: theme.font, textDecoration: 'underline',
};

const cancelBtn: React.CSSProperties = {
  flex: 1, padding: '10px 0', borderRadius: 10,
  background: theme.surface, border: `1px solid ${theme.border}`,
  color: theme.textDim, fontSize: 14, fontWeight: 600,
  cursor: 'pointer', fontFamily: theme.font,
};

const submitBtnStyle: React.CSSProperties = {
  flex: 2, padding: '10px 0', borderRadius: 10,
  background: theme.accent, border: 'none',
  color: theme.accentInk, fontSize: 14, fontWeight: 700,
  cursor: 'pointer', fontFamily: theme.font,
};
